import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import Lozenge from '@atlaskit/lozenge';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Skeleton from '@atlaskit/skeleton';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { AssessmentRecommendationTrustedHtml } from '../assessment-recommendation-trusted-html';

import { messages } from './messages';

import type {
	FastshiftAssessmentResult,
	FastshiftDcInstanceDetails,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

type TopRecommendation = NonNullable<FastshiftDcInstanceDetails['assessment']['topRecommendation']>;

export type TopRecommendationSectionProps = {
	isLoading: boolean;
	topRecommendation: TopRecommendation | null;
	results: FastshiftAssessmentResult[];
	onViewDetails: (recommendationId: string) => void;
};

const styles = cssMap({
	section: {
		width: '100%',
	},
	card: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		paddingBlock: token('space.300'),
		paddingInline: token('space.300'),
	},
	populatedCard: {
		border: `${token('border.width')} solid ${token('color.border.discovery')}`,
		borderRadius: token('radius.large'),
		paddingBlock: token('space.300'),
		paddingInline: token('space.300'),
	},
	centeredCardContent: {
		paddingTop: token('space.600'),
		paddingBottom: token('space.600'),
	},
	iconContainer: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
	},
	actions: {
		paddingTop: token('space.100'),
	},
});

export const TopRecommendationSection = ({
	isLoading,
	topRecommendation,
	results,
	onViewDetails,
}: TopRecommendationSectionProps) => {
	const { formatMessage } = useIntl();
	const recommendationCount = results.filter(({ hasRecommendations }) => hasRecommendations).length;

	return (
		<Box as="section" xcss={styles.section} aria-busy={isLoading}>
			<Stack space="space.200">
				<Stack space="space.100">
					<Inline space="space.100" alignBlock="center">
						<Heading size="small" as="h2">
							{formatMessage(messages.sectionTitle)}
						</Heading>
						{!isLoading && <Badge>{recommendationCount}</Badge>}
					</Inline>

					{isLoading ? (
						<Stack space="space.100">
							<Skeleton
								height="20px"
								width="100%"
								isShimmering
								testId="top-recommendation-description-skeleton"
							/>
							<Skeleton
								height="20px"
								width="55%"
								isShimmering
								testId="top-recommendation-description-skeleton-secondary"
							/>
						</Stack>
					) : topRecommendation ? (
						<Text>{formatMessage(messages.sectionDescription, { recommendationCount })}</Text>
					) : null}
				</Stack>

				{isLoading ? (
					<Box xcss={styles.card} testId="top-recommendation-loading-card">
						<Inline alignBlock="center" alignInline="center" xcss={styles.centeredCardContent}>
							<Spinner
								interactionName="load-top-recommendation"
								label={formatMessage(messages.loadingLabel)}
								size="large"
							/>
						</Inline>
					</Box>
				) : topRecommendation ? (
					<Box xcss={styles.populatedCard}>
						<Stack space="space.250">
							<Inline space="space.150" alignBlock="center" shouldWrap>
								<Box xcss={styles.iconContainer}>
									<LightbulbIcon label="" color={token('color.icon.discovery')} />
								</Box>
								<Lozenge appearance="discovery">{formatMessage(messages.startHere)}</Lozenge>
								<Text>
									{formatMessage(messages.recommendationPosition, { recommendationCount })}
								</Text>
							</Inline>

							<Stack space="space.150">
								<Heading size="small" as="h3">
									{topRecommendation.title}
								</Heading>
								{topRecommendation.description && (
									<AssessmentRecommendationTrustedHtml html={topRecommendation.description} />
								)}
							</Stack>

							<Inline alignInline="end" xcss={styles.actions}>
								<Button
									appearance="default"
									iconAfter={ArrowRightIcon}
									onClick={() => onViewDetails(topRecommendation.id)}
								>
									{formatMessage(messages.viewFullDetails)}
								</Button>
							</Inline>
						</Stack>
					</Box>
				) : (
					<Box xcss={styles.card}>
						<Stack alignInline="center" space="space.200" xcss={styles.centeredCardContent}>
							<Box xcss={styles.iconContainer}>
								<LightbulbIcon label="" color={token('color.icon')} />
							</Box>
							<Text align="center">{formatMessage(messages.emptyState)}</Text>
						</Stack>
					</Box>
				)}
			</Stack>
		</Box>
	);
};
